import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Button,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { Period } from "modules/Clinical/TimePoints/helpers/timpoints.types";

import { RowReasonTarget } from "../helper/manualEntry.types";

interface SingleRowReasonModalProps {
  isOpen: boolean;
  toggle: () => void;
  target: RowReasonTarget | null;
  onSubmit: (reason: string) => void;
  isPending: boolean;
}

const SingleRowReasonModal: React.FC<SingleRowReasonModalProps> = ({
  isOpen,
  toggle,
  target,
  onSubmit,
  isPending,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<{ reason: string }>({
    defaultValues: { reason: "" },
  });

  const watchedReason = watch("reason") || "";

  useEffect(() => {
    if (isOpen) {
      reset({ reason: "" });
    }
  }, [isOpen, reset]);

  if (!target) return null;

  const formFields: FormFieldConfig[] = [
    {
      name: "reason",
      label: "Reason for Correction",
      type: "textarea",
      required: true,
      placeholder: "Provide explanation for this correction...",
      rules: {
        required: "Reason for correction is required",
        minLength: { value: 3, message: "Reason must be at least 3 characters" },
      },
    },
  ];

  const onFormSubmit = (data: { reason: string }) => {
    onSubmit(data.reason);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="bg-light border-bottom p-3">
        <div className="d-flex align-items-center gap-2">
          <i className="ri-history-line text-primary fs-18"></i>
          <span className="fw-bold fs-15">Sample Collection Time Correction</span>
        </div>
      </ModalHeader>
      <ModalBody className="p-3">
        <div className="bg-light p-3 rounded-3 mb-3 border">
          <Row className="g-2 text-dark fs-12 mb-2">
            <Col xs={4}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Period</span>
              <strong className="fs-12">
                {target.period.period_name ||
                  target.period.periodname ||
                  `Period ${target.period.period_number}`}
              </strong>
            </Col>
            <Col xs={4}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Time Point</span>
              <strong className="fs-12">{target.row.timepoint}</strong>
            </Col>
            <Col xs={4}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Actual Schedule Time</span>
              <strong className="fs-12">{target.row.actualTime || "--"}</strong>
            </Col>
          </Row>
          <Row className="g-2 text-dark fs-12 border-top pt-2">
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Previous Collection Time</span>
              <span className="text-danger fw-semibold">{target.prevValue}</span>
            </Col>
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">New Collection Time</span>
              <span className="text-success fw-bold">{target.newValue}:00</span>
            </Col>
          </Row>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <CommonForm
            fields={formFields}
            control={control}
            errors={errors}
            columns={1}
            setValue={setValue}
            getValues={getValues}
          />
          <div className="d-flex justify-content-end mt-1 mb-2">
            <span className="text-muted fs-11">{250 - watchedReason.length} characters left</span>
          </div>

          <div className="d-flex justify-content-end gap-2 border-top pt-3">
            <Button
              type="button"
              color="light"
              className="border btn-sm px-3"
              onClick={toggle}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              className="btn-sm px-3"
              disabled={!watchedReason.trim() || isPending}
            >
              {isPending ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1"></i> Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default React.memo(SingleRowReasonModal);
